import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AspectRatio, MediaGeneratorKey } from '@mg/shared';
import { ASPECT_RATIOS, defaultModelFor, findModel, ratiosFor } from '@mg/shared';

/** Bumped when the stored shape changes, so an old entry is ignored rather than mis-read. */
const VERSION = 1;
const KEY = 'mg.generateMedia.prefs.v' + VERSION;

export interface GeneratorPreferences {
  generator: MediaGeneratorKey;
  modelId: string;
  aspect: AspectRatio;
  megapixels: number;
}

/**
 * Remember the last Generator / Model / Aspect ratio / Resolution across modal opens.
 *
 * VALIDATED ON READ, never trusted. Everything stored here can go stale between sessions: a BYO
 * provider's key gets removed, the install switches its built-in generator off, a model leaves the
 * registry, a model that used to offer 4 MP stops.
 * Restoring any of those verbatim would put the form into a state the server rejects — and the
 * rejection would arrive as a 400 on Generate, long after the choice was silently restored. So a
 * stored value survives only if the registry still agrees with it, and otherwise falls back the
 * same way a fresh modal would.
 *
 * Kept per browser rather than per workspace on purpose: this is a working preference about how you
 * like to generate, not workspace configuration, and it should follow you between brands.
 *
 * The prompt and the reference images are deliberately NOT stored. Reopening the modal to find last
 * week's prompt already in the box invites generating it again by accident, and a stored reference
 * id can point at an image that has since been deleted.
 */
export function useGeneratorPreferences(available: MediaGeneratorKey[]): {
  prefs: GeneratorPreferences;
  remember: (next: Partial<GeneratorPreferences>) => void;
} {
  const [stored, setStored] = useState<Partial<GeneratorPreferences>>(() => read());

  const prefs = useMemo(() => resolve(stored, available), [stored, available]);

  // Write back whatever validation settled on, so a value that has gone stale is corrected on disk
  // rather than being re-validated (and re-corrected) on every open.
  useEffect(() => {
    write(prefs);
  }, [prefs]);

  const remember = useCallback((next: Partial<GeneratorPreferences>) => {
    setStored((prev) => ({ ...prev, ...next }));
  }, []);

  return { prefs, remember };
}

/**
 * Reconcile what was stored against what the registry currently offers.
 *
 * Order matters: the generator decides which models exist, the model decides which ratios and
 * megapixel tiers are legal. Validating them independently would happily produce "Magnific at 4 MP".
 *
 * A generator no longer on offer falls back to the FIRST one that is (`available` is in dropdown
 * order): the built-in FLUX while the install has it switched on, as always, else the first BYO
 * provider. A remembered 'azure' must not come back on an install that has image generation off.
 * 'azure' only when nothing at all is on offer (FLUX off, and no provider key, or none known yet),
 * where the dialog has nothing to generate with either way.
 */
function resolve(
  stored: Partial<GeneratorPreferences>,
  available: MediaGeneratorKey[],
): GeneratorPreferences {
  const generator =
    stored.generator && available.includes(stored.generator)
      ? stored.generator
      : (available[0] ?? 'azure');

  // findModel falls back to the generator's default when the id is unknown, which is exactly the
  // behaviour wanted for a model that has since been removed.
  const model = findModel(generator, stored.modelId) ?? defaultModelFor(generator);
  const modelId = model?.id ?? '';

  const allowedRatios = model ? ratiosFor(model) : [...ASPECT_RATIOS];
  const aspect =
    stored.aspect && allowedRatios.includes(stored.aspect)
      ? stored.aspect
      : (allowedRatios[0] ?? '1:1');

  const tiers = model?.caps.megapixels ?? [1, 2];
  const megapixels =
    stored.megapixels != null && tiers.includes(stored.megapixels)
      ? stored.megapixels
      : // Prefer the middle of what's on offer over the smallest — the previous default was 2 MP.
        (tiers.includes(2) ? 2 : tiers[0]);

  return { generator, modelId, aspect, megapixels };
}

function read(): Partial<GeneratorPreferences> {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const r = parsed as Record<string, unknown>;
    // Field-by-field, with types checked — a hand-edited or half-written entry must not throw on
    // the way in and take the whole modal down with it.
    return {
      generator: typeof r.generator === 'string' ? (r.generator as MediaGeneratorKey) : undefined,
      modelId: typeof r.modelId === 'string' ? r.modelId : undefined,
      aspect: typeof r.aspect === 'string' ? (r.aspect as AspectRatio) : undefined,
      megapixels: typeof r.megapixels === 'number' ? r.megapixels : undefined,
    };
  } catch {
    // Unavailable (private mode, blocked storage) or unparseable — a forgotten preference is a
    // cosmetic loss, so there is nothing here worth surfacing to the user.
    return {};
  }
}

function write(prefs: GeneratorPreferences): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // Same reasoning as read().
  }
}
